import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AssistantConversationDTO, AssistantPendingDTO, ExtCardField } from '@contracts/assistant';
import type { Id } from '@contracts/common';
import { ApiClientError, offlineError } from '../../api/client';
import { TEXT } from '../../components/typography';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { NativeSelect } from '../../components/ui/native-select';
import { SWITCH } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import { avatarTone } from '../assistant-admin/server-tone';
import { useAssistantOptional } from './AssistantProvider';
import { conversationKey, editAction, useOwnAppearance } from './api';
import { EXT_CARD_LABELS as L, extErrorText } from './ext-card-labels';
import { Hana } from './Hana';
import { reasonLine } from './autonomy/labels';

/**
 * What became of an external card's Xác nhận (spec §7.3, §7.7, D47): out (`sending`), answered (`executed`, `failed`
 * with its code), or refused while the card stays pending (`refused`, the line the card shows).
 */
export type ExtOutcome =
  | { state: 'sending' } | { state: 'executed' } | { state: 'failed'; code: string }
  | { state: 'refused'; message: string };

/** The mockup's `.confirm`: a plain bordered card, its parts ruled apart (section 3). */
const BOX = 'mr-auto flex w-full flex-col overflow-hidden rounded-[10px] border border-[hsl(var(--h-border))] '
  + 'bg-[hsl(var(--h-card))] text-[13px] text-[hsl(var(--h-card-foreground))]';
/** `.btn.s`: 26 px under a fine pointer, the 44 px floor under a thumb. */
const SMALL = 'h-auto min-h-11 rounded-md px-2.5 py-1 text-xs font-semibold lg:min-h-[26px] lg:py-0';
const PRIMARY = `${SMALL} border border-[hsl(var(--h-primary))]`;
const PLAIN = `${SMALL} bg-[hsl(var(--h-card))]`;
const KEY = 'text-[11px] font-bold uppercase tracking-[.04em] text-[hsl(var(--h-muted-foreground))]';
const MUTED = 'text-[hsl(var(--h-muted-foreground))]';
/** A value read in a scroll box (§7.7: whole, at most 240 px tall) rather than inline. */
const LONG_AT = 120;
const isLong = (v: unknown): v is string => typeof v === 'string' && (v.length > LONG_AT || v.includes('\n'));
const FAILED = 'Đã có lỗi xảy ra, vui lòng thử lại';

function Empty() {
  return <span className={MUTED}>{L.empty}</span>;
}

/** A scalar as the person reads it: a boolean as Có/Không, nothing as (trống). */
function scalar(v: unknown): ReactNode {
  if (v === null || v === undefined || v === '') return <Empty />;
  if (typeof v === 'boolean') return v ? L.yes : L.no;
  return textOf(v);
}

/** A leaf as text; an object that reached here (never for a scalar field) as its JSON, not "[object Object]". */
const textOf = (v: unknown): string => (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v));

/** §7.7: a nested value as a read-only tree — keys muted and bold, arrays numbered — never one line of JSON. */
function TreeValue({ value }: { value: unknown }) {
  if (value === null || typeof value !== 'object') return <>{scalar(value)}</>;
  if (Array.isArray(value)) {
    if (value.length === 0) return <Empty />;
    return (
      <ol className="flex list-decimal flex-col gap-0.5 pl-5">
        {(value as unknown[]).map((v, i) => <li key={i} className="min-w-0 break-words"><TreeValue value={v} /></li>)}
      </ol>
    );
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return <Empty />;
  return (
    <ul className="flex flex-col gap-0.5">
      {entries.map(([k, v]) => {
        const nested = v !== null && typeof v === 'object';
        return (
          <li key={k} className="min-w-0 break-words">
            <span className={`font-semibold ${MUTED}`}>{k}</span>
            {nested ? <div className="pl-3"><TreeValue value={v} /></div> : <>{': '}<span>{scalar(v)}</span></>}
          </li>
        );
      })}
    </ul>
  );
}

function Value({ field }: { field: ExtCardField }) {
  if (field.kind === 'tree') return <TreeValue value={field.value} />;
  return <>{scalar(field.value)}</>;
}

/** Every field, in the server's order (§7.7: none hidden). */
function Fields({ fields }: { fields: readonly ExtCardField[] }) {
  if (fields.length === 0) return null;
  return (
    <dl className="flex flex-col gap-2 px-3 py-2">
      {fields.map((f) => (
        <div key={f.key} className="flex min-w-0 flex-col gap-0.5">
          <dt className={KEY}>{f.label}</dt>
          <dd className={cn('min-w-0 break-words leading-normal whitespace-pre-wrap',
            isLong(f.value) && 'max-h-[240px] overflow-auto rounded-md bg-[hsl(var(--h-muted))] px-2.5 py-2')}>
            <Value field={f} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

type Draft = Record<string, string | boolean>;
const draftOf = (f: ExtCardField): string | boolean => {
  if (f.kind === 'boolean') return f.value === true;
  if (f.kind === 'enum') return String(f.options?.findIndex((o) => o === f.value) ?? -1);
  return f.value === null || f.value === undefined ? '' : textOf(f.value);
};

/** The keys the person changed, as the schema types them; `null` for a number that is not one. */
function changedArgs(fields: readonly ExtCardField[], draft: Draft): { args: Record<string, unknown>; bad: string[] } {
  const args: Record<string, unknown> = {};
  const bad: string[] = [];
  for (const f of fields) {
    const v = draft[f.key];
    if (v === undefined || v === draftOf(f)) continue;
    if (f.kind === 'boolean') args[f.key] = v === true;
    else if (f.kind === 'enum') args[f.key] = f.options?.[Number(v)];
    else if (f.kind === 'number' || f.kind === 'integer') {
      const n = Number(v);
      if (String(v).trim() === '' || !Number.isFinite(n) || (f.kind === 'integer' && !Number.isInteger(n))) bad.push(f.key);
      else args[f.key] = n;
    } else args[f.key] = v;
  }
  return { args, bad };
}

/** `Sửa` of an external card (§7.7): the top-level scalars in place, the rest read-only; Lưu sends the changed keys. */
function ExtEditForm({ pending, fields, onDone, onEdited }: {
  pending: AssistantPendingDTO; fields: readonly ExtCardField[]; onDone: () => void;
  onEdited?: (next: AssistantPendingDTO) => void;
}) {
  const queryClient = useQueryClient();
  const conversationId = useAssistantOptional()?.conversationId ?? null;
  const [draft, setDraft] = useState<Draft>(() =>
    Object.fromEntries(fields.filter((f) => f.editable).map((f) => [f.key, draftOf(f)])));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // One request id per open form: a double click on Lưu is one edit.
  const requestId = useRef(crypto.randomUUID());
  const base = useId();

  const save = async () => {
    if (saving) return;
    const { args, bad } = changedArgs(fields, draft);
    if (bad.length > 0) {
      setErrors(Object.fromEntries(bad.map((k) => [k, 'Nhập một số hợp lệ'])));
      return;
    }
    if (Object.keys(args).length === 0) { onDone(); return; }
    setSaving(true);
    setErrors({});
    setFailure(null);
    try {
      const next = await editAction(pending.actionId, { input: { args } }, requestId.current);
      if (conversationId !== null) {
        queryClient.setQueryData<AssistantConversationDTO>(conversationKey(conversationId), (c) => (c === undefined ? c
          : { ...c, pending: c.pending.map((p) => (p.actionId === pending.actionId ? next : p)) }));
      }
      onEdited?.(next);
      onDone();
    } catch (e) {
      const err = e instanceof ApiClientError ? e : offlineError();
      const known = Object.entries(err.fields ?? {}).filter(([k]) => Object.hasOwn(draft, k));
      if (err.code === 'VALIDATION' && known.length > 0) setErrors(Object.fromEntries(known));
      else setFailure(err.message || FAILED);
      requestId.current = crypto.randomUUID();
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="flex flex-col gap-2 px-3 py-2" onSubmit={(e) => { e.preventDefault(); void save(); }}>
      {fields.map((f) => {
        if (!f.editable) {
          return (
            <div key={f.key} className="flex min-w-0 flex-col gap-0.5">
              <p className={KEY}>{f.label}</p>
              <div className="min-w-0 break-words whitespace-pre-wrap"><Value field={f} /></div>
            </div>
          );
        }
        const id = `${base}-${f.key}`;
        const error = errors[f.key];
        const errorId = error === undefined ? undefined : `${id}-error`;
        const set = (v: string | boolean) => setDraft((d) => ({ ...d, [f.key]: v }));
        const common = { id, 'aria-invalid': error === undefined ? undefined : true, 'aria-describedby': errorId } as const;
        const value = draft[f.key];
        return (
          <div key={f.key} className="flex flex-col gap-1">
            {f.kind === 'boolean' ? (
              <label htmlFor={id} className="flex items-center justify-between gap-3">
                <span className={KEY}>{f.label}</span>
                <input type="checkbox" role="switch" className={SWITCH} {...common} checked={value === true}
                  onChange={(e) => set(e.target.checked)} />
              </label>
            ) : (
              <>
                <Label htmlFor={id} className={KEY}>{f.label}</Label>
                {f.kind === 'enum' ? (
                  <NativeSelect {...common} value={String(value)} onChange={(e) => set(e.target.value)}>
                    {String(value) === '-1' && <option value="-1">—</option>}
                    {f.options?.map((o, i) => <option key={i} value={String(i)}>{String(o)}</option>)}
                  </NativeSelect>
                ) : f.kind === 'number' || f.kind === 'integer' ? (
                  <Input {...common} type="number" inputMode={f.kind === 'integer' ? 'numeric' : 'decimal'}
                    step={f.kind === 'integer' ? 1 : 'any'} value={String(value)} onChange={(e) => set(e.target.value)} />
                ) : isLong(f.value) ? (
                  <Textarea {...common} rows={6} value={String(value)} onChange={(e) => set(e.target.value)} />
                ) : (
                  <Input {...common} value={String(value)} onChange={(e) => set(e.target.value)} />
                )}
              </>
            )}
            {error !== undefined && <p id={errorId} className={TEXT.fieldError}>{error}</p>}
          </div>
        );
      })}
      {failure !== null && <p role="alert" className={TEXT.fieldError}>{failure}</p>}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" className={PRIMARY} disabled={saving}>{L.save}</Button>
        <Button type="button" variant="outline" className={PLAIN} onClick={onDone}>{L.cancel}</Button>
      </div>
    </form>
  );
}

/** "còn m:ss" until the card's 10 minutes are up (§7.7), every second. */
function Countdown({ expiresAt }: { expiresAt: string }) {
  const end = Date.parse(expiresAt);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!(end > now)) return undefined;
    const timer = window.setTimeout(() => setNow(Date.now()), 1000 - (now % 1000) || 1000);
    return () => window.clearTimeout(timer);
  }, [end, now]);
  return <span className={`text-xs tabular-nums ${MUTED}`}>{end > now ? L.left(end - now) : L.expired}</span>;
}

/** After Xác nhận (§7.3): the card's last line in place of its buttons. */
function After({ outcome, server }: { outcome: Exclude<ExtOutcome, { state: 'refused' }>; server: string }) {
  const still = useAssistantOptional()?.still ?? false;
  const line = 'flex flex-wrap items-center gap-x-2 gap-y-0.5 border-t border-[hsl(var(--h-border))] px-3 py-2.5 text-xs';
  if (outcome.state === 'sending') {
    return (
      <p role="status" className={line}>
        <span aria-hidden="true" className={cn('inline-block size-3 rounded-full border-2 border-[hsl(var(--h-icon-violet)/0.3)]',
          'border-t-[hsl(var(--h-icon-violet))]', !still && 'animate-spin motion-reduce:animate-none')} />
        <span>{L.sending(server)}</span>
      </p>
    );
  }
  if (outcome.state === 'executed') {
    return (
      <p role="status" className={`${line} font-semibold text-[hsl(var(--h-success-fg))]`}>
        <span aria-hidden="true">✓</span><span>{L.sent}</span>
      </p>
    );
  }
  if (outcome.code === 'EXT_UNKNOWN') {
    return <p role="status" className={`${line} font-semibold text-[hsl(var(--h-warning-fg))]`}>{L.unknown(server)}</p>;
  }
  return (
    <p role="status" className={line}>
      <span className="font-semibold text-[hsl(var(--h-error-fg))]">{L.failed}</span>
      <span className={MUTED}>{extErrorText(outcome.code, server, '')}</span>
    </p>
  );
}

/**
 * The external confirm card (spec §7.7, §7.4, D44; mockup section 3): the server's square, "<server> · <tool>" and
 * Hana asking; the warn band (`send` warn colours, a raised read in info colours); every arg; Xác nhận · Sửa (only when
 * something is editable) · Từ chối and the countdown. After Xác nhận the buttons give way to the outcome. Never a
 * Hoàn tác: an outside write cannot be taken back.
 */
export function ExtConfirmCard({ pending, disabled, onAct, outcome, onEdited, className }: {
  pending: AssistantPendingDTO & { external: NonNullable<AssistantPendingDTO['external']> };
  disabled: boolean; onAct: (actionId: Id, kind: 'confirm' | 'reject') => void; outcome?: ExtOutcome;
  onEdited?: (next: AssistantPendingDTO) => void; className?: string;
}) {
  const appearance = useOwnAppearance();
  const ext = pending.external;
  const [editing, setEditing] = useState(false);
  const still = useAssistantOptional()?.still ?? false;
  const after = outcome !== undefined && outcome.state !== 'refused' ? outcome : null;
  const refused = outcome?.state === 'refused' ? outcome.message : null;
  const send = ext.warn === 'send';
  const reason = reasonLine(pending.reason, pending.taint, pending.peerTaint);

  return (
    <div role="group" aria-label="Cần xác nhận" data-action={pending.actionId} data-external="" className={cn(BOX, className)}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span data-srv="" aria-hidden="true" className={cn('grid size-[22px] flex-none place-items-center rounded-md text-[11px] font-extrabold text-white',
          avatarTone(ext.serverId ?? ext.serverName))}>{ext.initial}</span>
        <b className="min-w-0 flex-1 break-words">{`${ext.serverName} · ${ext.toolTitle}`}</b>
        <Hana appearance={appearance} emotion="ask" size={22} still={still} />
      </div>
      <p data-warn={ext.warn} className={cn('mx-3 rounded-md px-2.5 py-2 text-xs leading-normal', send
        ? 'bg-[hsl(var(--h-warning-soft))] text-[hsl(var(--h-warning-fg))]'
        : 'bg-[hsl(var(--h-info-soft))] text-[hsl(var(--h-info-fg))]')}>
        {send ? L.warnSend(ext.serverName) : L.warnRaised(ext.serverName)}
      </p>
      {/* S7 §9.5: why autonomy asked; D13: a tainted reason only where no taint line says it already. */}
      {reason !== null && <p data-reason={pending.reason?.code} className="mx-3 rounded-md bg-[hsl(var(--h-info-soft))] px-2.5 py-2 text-xs leading-normal text-[hsl(var(--h-info-fg))]">{reason}</p>}
      {editing && after === null
        ? <ExtEditForm pending={pending} fields={ext.fields} onEdited={onEdited} onDone={() => setEditing(false)} />
        : <Fields fields={ext.fields} />}
      {refused !== null && <p role="alert" className={`px-3 pb-2 ${TEXT.fieldError} text-xs`}>{refused}</p>}
      {after !== null ? <After outcome={after} server={ext.serverName} /> : !editing && (
        <div className="flex flex-wrap items-center gap-2 border-t border-[hsl(var(--h-border))] px-3 py-2.5">
          <Button type="button" className={PRIMARY} disabled={disabled}
            onClick={() => onAct(pending.actionId, 'confirm')}>{L.confirm}</Button>
          {ext.editable && (
            <Button type="button" variant="outline" className={PLAIN} disabled={disabled}
              onClick={() => setEditing(true)}>{L.edit}</Button>
          )}
          <Button type="button" variant="outline" className={PLAIN} disabled={disabled}
            onClick={() => onAct(pending.actionId, 'reject')}>{L.reject}</Button>
          <span className="flex-1" />
          <Countdown expiresAt={pending.expiresAt} />
        </div>
      )}
    </div>
  );
}
